import 'svelte/internal/disclose-version';
import * as $ from 'svelte/internal/client';
import { onDestroy } from 'svelte';
import * as pipelineModel from '../../../../features/pipeline/pipeline-model.ts';
import { getLanguage, subscribeLanguageChange, t } from '../../i18n.ts';
import { MATERIAL_PRESETS } from '../../ui/preview-presets.ts';
import DropdownMenu from '../svelte-dropdown-menu.svelte';

import {
	clamp,
	cloneMaterialSettings,
	getMaterialRangeStep,
	isValidMaterialSettings
} from './shared.ts';

var root = $.from_html(`<span class="ui-symbol-kebab">...</span>`);
var root_1 = $.from_html(`<button type="button" class="ui-menu-item" role="menuitem"> </button>`);
var root_2 = $.from_html(`<div class="ui-menu-header"> </div> <!>`, 1);
var root_3 = $.from_html(`<label class="field svelte-d5unid"><span class="label-row svelte-d5unid"><span class="label-text svelte-d5unid"> </span> <span class="value-text svelte-d5unid"> </span></span> <input class="range-input svelte-d5unid" type="range"/></label>`);
var root_4 = $.from_html(`<div class="panel-root svelte-d5unid"><div class="panel-head svelte-d5unid"><div><div class="section-label"> </div> <div class="help-text svelte-d5unid"> </div></div> <!></div> <div class="grid svelte-d5unid"><label class="field color-field svelte-d5unid"><span class="label-row svelte-d5unid"><span class="label-text svelte-d5unid"> </span> <span class="value-text svelte-d5unid" id="mat-base-color-value"> </span></span> <input class="color-input svelte-d5unid" type="color" id="mat-base-color"/></label> <!></div></div>`);

const $$css = {
	hash: 'svelte-d5unid',
	code: '.panel-root.svelte-d5unid {display:flex;flex-direction:column;gap:10px;}.panel-head.svelte-d5unid {display:flex;justify-content:space-between;gap:8px;}.help-text.svelte-d5unid {font-size:11px;color:var(--color-muted);}.grid.svelte-d5unid {display:grid;grid-template-columns:repeat(2, minmax(0, 1fr));gap:8px;}.field.svelte-d5unid {display:flex;flex-direction:column;gap:6px;padding:9px 10px;border:1px solid var(--color-panel-border-strong, #464646);border-radius:12px;background:color-mix(in srgb, var(--color-panel), var(--color-bg) 4%);}.color-field.svelte-d5unid {grid-column:span 2;}.label-row.svelte-d5unid {display:flex;align-items:center;justify-content:space-between;gap:10px;}.label-text.svelte-d5unid {font-size:11px;font-weight:700;color:var(--color-text-strong);}.value-text.svelte-d5unid {font-family:var(--font-mono, monospace);font-size:11px;color:var(--color-muted);}.color-input.svelte-d5unid {width:100%;height:34px;border:1px solid var(--color-control-border, var(--color-line));border-radius:9px;background:var(--color-control-bg, var(--color-panel));padding:4px;}.range-input.svelte-d5unid {width:100%;accent-color:var(--color-accent);}\n\n  @media (max-width: 1180px) {.grid.svelte-d5unid {grid-template-columns:1fr;}.color-field.svelte-d5unid {grid-column:span 1;}\n  }\n\n  @media (max-width: 900px) {.panel-head.svelte-d5unid {flex-direction:column;align-items:flex-start;}\n  }'
};

export default function Svelte_material_panel($$anchor, $$props) {
	$.push($$props, true);
	$.append_styles($$anchor, $$css);

	const settings = $.prop($$props, 'settings', 23, () => cloneMaterialSettings(pipelineModel.DEFAULT_MATERIAL_SETTINGS));

	function dispatch(eventName, detail) {
		$$props.$$host.dispatchEvent(new CustomEvent(eventName, { detail, bubbles: true, composed: true }));
	}

	let language = $.state($.proxy(getLanguage()));

	const disposeLanguageSync = subscribeLanguageChange((nextLanguage) => {
		$.set(language, nextLanguage, true);
	});

	onDestroy(() => {
		disposeLanguageSync();
	});

	function tr(key, values) {
		$.get(language);

		return values ? t(key, values) : t(key);
	}

	function handleBaseColorInput(event) {
		const input = event.currentTarget;

		if (!input) {
			dispatch('status-message', { message: tr('panel.baseColorInputMissing'), kind: 'error' });

			return;
		}

		const parsed = pipelineModel.parseHexColor(input.value);

		if (!parsed) {
			dispatch('status-message', { message: tr('panel.baseColorInvalid'), kind: 'error' });

			return;
		}

		dispatch('settings-change', {
			settings: { ...settings(), baseColor: [parsed[0], parsed[1], parsed[2]] }
		});
	}

	function handleRangeInput(event, binding) {
		const input = event.currentTarget;

		if (!input) {
			dispatch('status-message', {
				message: tr('panel.rangeInputMissing', { label: binding.label }),
				kind: 'error'
			});

			return;
		}

		const parsed = Number(input.value);

		if (!Number.isFinite(parsed)) {
			dispatch('status-message', {
				message: tr('panel.rangeInvalid', { label: binding.label }),
				kind: 'error'
			});

			return;
		}

		const next = cloneMaterialSettings(settings());

		next[binding.key] = clamp(parsed, binding.min, binding.max);
		dispatch('settings-change', { settings: next });
	}

	function handleMaterialRangeWheel(event, binding) {
		event.preventDefault();

		const step = Number(getMaterialRangeStep(binding.key));
		const next = cloneMaterialSettings(settings());
		const delta = event.deltaY < 0 ? step : -step;

		next[binding.key] = clamp(settings()[binding.key] + delta, binding.min, binding.max);
		dispatch('settings-change', { settings: next });
	}

	function applyMaterialPreset(preset) {
		if (!isValidMaterialSettings(preset.settings)) {
			dispatch('status-message', {
				message: tr('panel.status.materialPresetInvalidValue', { value: preset.key }),
				kind: 'error'
			});

			return false;
		}

		dispatch('settings-change', { settings: cloneMaterialSettings(preset.settings) });

		dispatch('status-message', {
			message: tr('panel.status.materialPresetApplied', { name: tr(preset.labelKey) }),
			kind: 'info'
		});

		return true;
	}

	const baseColorHex = $.derived(() => pipelineModel.colorToHex(settings().baseColor));

	var $$exports = {
		get settings() {
			return settings();
		},

		set settings(
			$$value = cloneMaterialSettings(pipelineModel.DEFAULT_MATERIAL_SETTINGS)
		) {
			settings($$value);
			$.flush();
		}
	};

	var fragment = $.comment();
	var node = $.first_child(fragment);

	$.key(node, () => $.get(language), ($$anchor) => {
		var div = root_4();
		var div_1 = $.child(div);
		var div_2 = $.child(div_1);
		var div_3 = $.child(div_2);
		var text = $.only_child(div_3, true);
		var div_4 = $.sibling(div_3, 2);
		var text_1 = $.only_child(div_4, true);

		$.reset(div_2);

		var node_1 = $.sibling(div_2, 2);

		{
			const trigger = ($$anchor) => {
				var span = root();

				$.append($$anchor, span);
			};

			const children = ($$anchor, closeMenu = $.noop) => {
				var fragment_1 = root_2();
				var div_5 = $.first_child(fragment_1);
				var text_2 = $.only_child(div_5, true);
				var node_2 = $.sibling(div_5, 2);

				$.each(node_2, 17, () => MATERIAL_PRESETS, (preset) => preset.labelKey, ($$anchor, preset) => {
					var button = root_1();
					var text_3 = $.only_child(button, true);

					$.template_effect(($0) => $.set_text(text_3, $0), [() => tr($.get(preset).labelKey)]);

					$.delegated('click', button, () => {
						if (applyMaterialPreset($.get(preset))) {
							closeMenu()();
						}
					});

					$.append($$anchor, button);
				});

				$.template_effect(($0) => $.set_text(text_2, $0), [() => tr('panel.materialPreset')]);
				$.append($$anchor, fragment_1);
			};

			let $0 = $.derived(() => tr('panel.materialPreset'));

			DropdownMenu(node_1, {
				wrapperClass: 'ui-menu-wrap',
				triggerClassName: 'ui-menu-trigger',
				menuClass: 'ui-menu',
				get triggerAriaLabel() {
					return $.get($0);
				},
				menuRole: 'menu',
				triggerVariant: 'menu-trigger',
				trigger,
				children,
				$$slots: { trigger: true, default: true }
			});
		}

		$.reset(div_1);

		var div_6 = $.sibling(div_1, 2);
		var label = $.child(div_6);
		var span_1 = $.child(label);
		var span_2 = $.child(span_1);
		var text_4 = $.only_child(span_2, true);
		var span_3 = $.sibling(span_2, 2);
		var text_5 = $.only_child(span_3, true);

		$.reset(span_1);

		var input_1 = $.sibling(span_1, 2);

		$.remove_input_defaults(input_1);
		$.reset(label);

		var node_3 = $.sibling(label, 2);

		$.each(node_3, 17, () => pipelineModel.MATERIAL_RANGE_BINDINGS, (binding) => binding.key, ($$anchor, binding) => {
			var label_1 = root_3();
			var span_4 = $.child(label_1);
			var span_5 = $.child(span_4);
			var text_6 = $.only_child(span_5, true);
			var span_6 = $.sibling(span_5, 2);
			var text_7 = $.only_child(span_6, true);

			$.reset(span_4);

			var input_2 = $.sibling(span_4, 2);

			$.remove_input_defaults(input_2);
			$.reset(label_1);

			$.template_effect(
				($0, $1, $2, $3, $4) => {
					$.set_text(text_6, $.get(binding).label);
					$.set_attribute(span_6, 'id', $.get(binding).outputId);
					$.set_text(text_7, $0);
					$.set_attribute(input_2, 'id', $.get(binding).inputId);
					$.set_attribute(input_2, 'min', $1);
					$.set_attribute(input_2, 'max', $2);
					$.set_attribute(input_2, 'step', $3);
					$.set_value(input_2, $4);
				},
				[
					() => settings()[$.get(binding).key].toFixed($.get(binding).fractionDigits),
					() => String($.get(binding).min),
					() => String($.get(binding).max),
					() => getMaterialRangeStep($.get(binding).key),
					() => String(settings()[$.get(binding).key])
				]
			);

			$.delegated('input', input_2, (event) => handleRangeInput(event, $.get(binding)));
			$.delegated('change', input_2, (event) => handleRangeInput(event, $.get(binding)));
			$.event('wheel', input_2, (event) => handleMaterialRangeWheel(event, $.get(binding)));
			$.append($$anchor, label_1);
		});

		$.reset(div_6);
		$.reset(div);

		$.template_effect(
			($0, $1, $2) => {
				$.set_text(text, $0);
				$.set_text(text_1, $1);
				$.set_text(text_4, $2);
				$.set_text(text_5, $.get(baseColorHex));
				$.set_value(input_1, $.get(baseColorHex));
			},
			[
				() => tr('panel.materialTitle'),
				() => tr('panel.materialHelp'),
				() => tr('panel.baseColor')
			]
		);

		$.delegated('input', input_1, handleBaseColorInput);
		$.append($$anchor, div);
	});

	$.append($$anchor, fragment);

	return $.pop($$exports);
}

$.delegate(['click', 'input', 'change']);
customElements.define('lut-material-panel', $.create_custom_element(Svelte_material_panel, { settings: {} }, [], []));